import { Modal, type ModalProps } from "antd";
import React, { memo, type ReactNode } from "react";
import crossIconCustom from "sipli-fleet-core/assets/icons/custom-modal-cross-icon.svg?url";
import CustomButton from "../CustomSharedButton/CustomSharedButton";
import CustomSvgIcon from "../CustomSharedSvg/CustomSharedSvg";
import { cn } from "src/util/util.service";

type CustomButtonProps = React.ComponentProps<typeof CustomButton>;

export interface CustomModalProps extends Omit<
  ModalProps,
  "footer" | "onOk" | "onCancel"
> {
  children?: ReactNode;
  rootClassName?: string;
  closeIcon?: ReactNode;
  okText?: string;
  cancelText?: string;
  onOk?: () => void;
  onCancel?: () => void;
  okButtonProps?: Partial<CustomButtonProps>;
  cancelButtonProps?: Partial<CustomButtonProps>;
  hideOkButton?: boolean;
  hideCancelButton?: boolean;
  showFooter?: boolean;
  footer?: ModalProps["footer"];
  okBtnLoading?: boolean;
}

function CustomModal({
  children = null,
  rootClassName = "",
  closeIcon,
  okText = "OK",
  cancelText = "Cancel",
  onOk,
  onCancel,
  okButtonProps = {},
  cancelButtonProps = {},
  hideOkButton = false,
  hideCancelButton = false,
  showFooter = true,
  footer: footerProp,
  okBtnLoading = false,
  ...restProps
}: CustomModalProps) {
  const footer = showFooter ? (
    <div className="flex justify-end gap-3 mt-4">
      {!hideCancelButton && (
        <CustomButton
          label={cancelText}
          buttonSize="medium"
          type="secondary"
          onClick={onCancel}
          {...cancelButtonProps}
        />
      )}

      {!hideOkButton && (
        <CustomButton
          label={okText}
          buttonSize="medium"
          type="primary"
          onClick={onOk}
          loading={okBtnLoading}
          {...okButtonProps}
        />
      )}
    </div>
  ) : null;

  return (
    <Modal
      {...restProps}
      footer={footerProp !== undefined ? footerProp : footer}
      closeIcon={
        closeIcon !== undefined ? (
          closeIcon
        ) : (
          <CustomSvgIcon src={crossIconCustom} className="cursor-pointer" />
        )
      }
      rootClassName={cn("custom-modal-styling-container", rootClassName)}
      focusTriggerAfterClose={false}
      onCancel={onCancel}
      onOk={onOk}
    >
      {children}
    </Modal>
  );
}

export default memo(CustomModal);
